import type { TElement } from "platejs";
import type { SelectableItem } from "../../../common/contexts/SelectionContext";
import type { AssemblyProject } from "../../../common/contexts/SelectionContext";
import { toSentenceCase } from "../../../../utils/formatLogText";
import { BLOCK_LOG, BLOCK_OTF, BLOCK_TRANSCRIPT, type CortesNode } from "./schema";

function textChildren(text: string | undefined | null): { text: string }[] {
  return [{ text: text ?? "" }];
}

// `formatLogText` (used by LogCard and friends) renders participant names
// in bold uppercase via inline `nameRegex` matching, plus brackets as
// uppercase tags. The Plate blocks have no display-time transform, so we
// bake those visual normalizations into the dropped text so Cortes mirrors
// what the user saw in the Library:
//
//   - bracket content → uppercase
//   - participant names (resolved against the project participant list) →
//     uppercase in body text
//   - "N-name" prefix (e.g. "17-arturo") → stripped, then uppercased
//
// All transforms are case-insensitive at match time and uppercasing is
// purely presentational — exports build their own `[LABEL]` strings
// literally, and `formatLogText` matching is case-insensitive, so no
// downstream parsing depends on the case in storage.
//
// `participantList` here is the same shape LogCard/formatLogText receive:
// project-level participant objects with `name`/`alias`. Per-item
// `item.participants` is intentionally NOT used here because its shape
// varies (IDs for logs, name strings for transcripts) and only the global
// list reliably maps to display names.
function uppercaseParticipantNames(text: string, participantList: any[] | undefined): string {
  if (!text) return text;
  // Strip "N-name" prefix (e.g. "17-carlos salcido" → "carlos salcido") to
  // match formatLogText's `numPrefixPattern` handling.
  let out = text.replace(
    /\b(\d{1,3})-([a-záéíóúñA-ZÁÉÍÓÚÑ]{2,}(?:\s[a-záéíóúñA-ZÁÉÍÓÚÑ]+)*)/gi,
    (_, _num, name) => name,
  );
  const tokens: string[] = [];
  for (const p of (participantList ?? []) as Array<{ name?: string; alias?: string }>) {
    for (const raw of [p?.name, p?.alias]) {
      const clean = (raw || "").replace(/^\d+-/, "").trim();
      if (clean && clean.length > 3 && !/^OTF_/i.test(clean) && !/^MIC_/i.test(clean)) {
        tokens.push(clean);
      }
    }
  }
  if (tokens.length === 0) return out;
  const sorted = [...new Set(tokens)].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  return out.replace(re, (m) => m.toUpperCase());
}

function uppercaseBrackets(text: string): string {
  return text.replace(/\[([^\]]+)\]/g, (_, inner) => `[${inner.toUpperCase()}]`);
}

// LogCard additionally sentence-cases the body, so logs typed in all-caps
// display sentence-cased in the Library. Logs go through that extra step;
// OTF/transcript text from STT is already proper-cased so we leave the
// non-name portion verbatim.
function normalizeLogText(
  text: string | undefined | null,
  participantList: any[] | undefined,
): string {
  const sentence = toSentenceCase(text ?? "");
  return uppercaseBrackets(uppercaseParticipantNames(sentence, participantList));
}

function normalizeSpokenText(
  text: string | undefined | null,
  participantList: any[] | undefined,
): string {
  return uppercaseBrackets(uppercaseParticipantNames(text ?? "", participantList));
}

function newId(): string {
  // crypto.randomUUID is available in modern browsers and Node 19+. Fallback
  // keeps tests/SSR environments without crypto from throwing.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Build a block from a SelectableItem, preserving every field so the round-trip
 * through `docToItems` does not drop data. The block `id` becomes the future
 * `assembly_items.id` row id — for items dropped from the Library that id is
 * NOT a DB UUID (it's the library entity id, possibly shared across siblings),
 * so we always mint a fresh UUID here. Hydration from a previously persisted
 * project uses `seedFromProject`, which overrides the id with the stored
 * `item.id` so upserts don't recreate rows referenced by summaries / FKs.
 * NodeIdPlugin only assigns ids when missing, so an explicit id is left
 * untouched on normalization.
 */
export function itemToBlock(item: SelectableItem, participantList?: any[]): CortesNode | null {
  const common = {
    id: newId(),
    sourceId: item.sourceId ?? item.id,
    tc: item.tc,
    author: item.author,
    tags: item.tags,
    cameras: item.cameras,
    rating: item.rating,
    participants: item.participants,
    tcIn: item.tcIn,
    tcOut: item.tcOut,
    tcInMs: item.tcInMs,
    tcOutMs: item.tcOutMs,
    speakerName: item.speakerName,
    speakerOrder: item.speakerOrder,
    selectedText: item.selectedText,
    sourceSegmentId: item.sourceSegmentId,
    storyId: item.storyId,
    directorId: item.directorId,
    rundownId: item.rundownId,
    rundownName: item.rundownName,
    thumbnail: item.thumbnail,
    duration: item.duration,
    title: item.title,
    // Preserve pre-promotion type so an OTF block dropped from OTF MODE
    // still knows whether it came from a log or a transcript.
    originType: item.originType,
    // ── Propagated story/event metadata ──
    // These travel with the item from the drag payload and must be
    // preserved so LogCard can display the full metadata panel.
    storyTags: (item as any).storyTags,
    storyParticipants: (item as any).storyParticipants,
    storyRating: (item as any).storyRating,
    storyName: (item as any).storyName,
    eventName: (item as any).eventName,
    entryTags: (item as any).entryTags,
    streams: (item as any).streams,
    relayChannel: (item as any).relayChannel,
    endAt: (item as any).endAt,
    startAt: (item as any).startAt || (item as any).start_at,
    createdAt: (item as any).createdAt || (item as any).created_at,
    storyDirectorId: (item as any).storyDirectorId,
    // Word-level timestamps for RESUMEN feature
    words: (item as any).words,
  };

  if (item.type === "log") {
    return {
      ...common,
      type: BLOCK_LOG,
      children: textChildren(normalizeLogText(item.title, participantList)),
    } as CortesNode;
  }
  if (item.type === "otf") {
    return {
      ...common,
      type: BLOCK_OTF,
      children: textChildren(normalizeSpokenText(item.selectedText ?? item.title, participantList)),
    } as CortesNode;
  }
  if (item.type === "transcript") {
    return {
      ...common,
      type: BLOCK_TRANSCRIPT,
      children: textChildren(normalizeSpokenText(item.selectedText ?? item.title, participantList)),
    } as CortesNode;
  }
  return null;
}

/**
 * Convert top-level editor blocks back into SelectableItems for persistence to
 * `assembly_items`. Non-source blocks (pleca / nota / checklist / paragraph /
 * h2) are skipped — they live exclusively in `cortes_doc`.
 */
export function docToItems(nodes: TElement[]): SelectableItem[] {
  const items: SelectableItem[] = [];
  for (const n of nodes) {
    const t = n.type;
    if (t !== BLOCK_LOG && t !== BLOCK_OTF && t !== BLOCK_TRANSCRIPT) continue;
    const anyNode = n as any;
    const textFromChildren = ((n.children ?? []) as Array<{ text?: string }>)
      .map((c) => (typeof c?.text === "string" ? c.text : ""))
      .join("");
    const itemType: SelectableItem["type"] =
      t === BLOCK_LOG ? "log" : t === BLOCK_OTF ? "otf" : "transcript";
    items.push({
      id: (anyNode.id as string) || newId(),
      type: itemType,
      title: anyNode.title || textFromChildren || "Sin título",
      thumbnail: anyNode.thumbnail,
      duration: anyNode.duration,
      sourceId: anyNode.sourceId,
      tc: anyNode.tc,
      author: anyNode.author,
      tags: anyNode.tags,
      cameras: anyNode.cameras,
      rating: anyNode.rating,
      participants: anyNode.participants,
      tcIn: anyNode.tcIn,
      tcOut: anyNode.tcOut,
      tcInMs: anyNode.tcInMs,
      tcOutMs: anyNode.tcOutMs,
      speakerName: anyNode.speakerName,
      speakerOrder: anyNode.speakerOrder,
      // For OTF/transcript the live text is in the Slate children, not the
      // snapshotted `selectedText` — read children first so user edits land.
      selectedText:
        itemType === "log" ? anyNode.selectedText : textFromChildren || anyNode.selectedText,
      sourceSegmentId: anyNode.sourceSegmentId,
      storyId: anyNode.storyId,
      directorId: anyNode.directorId,
      rundownId: anyNode.rundownId,
      rundownName: anyNode.rundownName,
      originType: anyNode.originType,
      // ── Propagated story/event metadata (round-trip) ──
      storyTags: anyNode.storyTags,
      storyParticipants: anyNode.storyParticipants,
      storyRating: anyNode.storyRating,
      storyName: anyNode.storyName,
      eventName: anyNode.eventName,
      entryTags: anyNode.entryTags,
      streams: anyNode.streams,
      relayChannel: anyNode.relayChannel,
      startAt: anyNode.startAt,
      createdAt: anyNode.createdAt,
      endAt: anyNode.endAt,
      storyDirectorId: anyNode.storyDirectorId,
    } as any);
    // words is not part of SelectableItem but we need it on the raw object
    // so it round-trips through cortesDoc → assembly_items → cortesDoc.
    // Attach it after the push so TypeScript doesn't complain.
    if (anyNode.words?.length) {
      (items[items.length - 1] as any).words = anyNode.words;
    }
  }
  return items;
}

/**
 * Initial value for the editor when opened on a given project. Prefers a
 * previously-persisted `cortesDoc` (canonical for the editor view) and falls
 * back to reconstructing blocks from `items` for projects that pre-date the
 * editor. Empty projects start with a single empty paragraph so the placeholder
 * shows and there is a cursor target.
 */
export function seedFromProject(project: AssemblyProject | null | undefined): TElement[] {
  if (project?.cortesDoc && Array.isArray(project.cortesDoc) && project.cortesDoc.length > 0) {
    const itemsById = new Map(project.items.map((item) => [item.id, item]));
    const itemsBySourceId = new Map(
      project.items.filter((item) => item.sourceId).map((item) => [item.sourceId as string, item]),
    );

    return (project.cortesDoc as TElement[]).map((node) => {
      if (node.type !== BLOCK_LOG && node.type !== BLOCK_OTF && node.type !== BLOCK_TRANSCRIPT) {
        return node;
      }

      const storedNode = node as TElement & Record<string, any>;
      const item =
        (storedNode.id ? itemsById.get(storedNode.id) : undefined) ||
        (storedNode.sourceId ? itemsBySourceId.get(storedNode.sourceId) : undefined);
      if (!item) return node;

      // `cortesDoc` is the canonical source for editable text and ordering, but
      // older snapshots predate relay playback metadata such as startAt,
      // streams and relayChannel. Backfill only missing values from the
      // persisted assembly item so opening an old CUT can still locate video.
      const itemBlock = itemToBlock(item) as TElement & Record<string, any>;
      const hydratedNode = { ...storedNode };
      for (const [key, value] of Object.entries(itemBlock)) {
        if (
          value != null &&
          value !== "" &&
          (hydratedNode[key] == null || hydratedNode[key] === "")
        ) {
          hydratedNode[key] = value;
        }
      }

      return hydratedNode;
    });
  }
  if (project?.items && project.items.length > 0) {
    const blocks = project.items
      .map((it): CortesNode | null => {
        const b = itemToBlock(it);
        // Preserve the persisted item id as the block id so the next save's
        // diff-based upsert (assembly-routes.js) updates the existing row
        // instead of deleting and recreating it.
        return b ? ({ ...b, id: it.id } as CortesNode) : null;
      })
      .filter((b): b is CortesNode => !!b) as TElement[];
    if (blocks.length > 0) return blocks;
  }
  return seedEmpty();
}

export function seedEmpty(): TElement[] {
  return [{ type: "p", children: textChildren("") }];
}
